import { bech32 } from "bech32";
import { secp256k1 } from "@noble/curves/secp256k1.js";
import { keccak_256 } from "@noble/hashes/sha3.js";
import { CHAIN } from "./config.mjs";

const SCALE = 10n ** BigInt(CHAIN.decimals);

export function displayToBase(value) {
  const input = String(value).trim();
  if (!/^\d+(?:\.\d+)?$/.test(input)) {
    throw new Error("Amount must be a positive decimal number");
  }
  const [whole, fraction = ""] = input.split(".");
  if (fraction.length > CHAIN.decimals) {
    throw new Error(`Amount supports at most ${CHAIN.decimals} decimals`);
  }
  const result = BigInt(whole) * SCALE
    + BigInt((fraction + "0".repeat(CHAIN.decimals)).slice(0, CHAIN.decimals));
  if (result <= 0n) throw new Error("Amount must be greater than zero");
  return result.toString();
}

export function baseToDisplay(value) {
  const amount = BigInt(value);
  const whole = amount / SCALE;
  const fraction = (amount % SCALE).toString().padStart(CHAIN.decimals, "0")
    .replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

export function validateAddress(address) {
  try {
    const decoded = bech32.decode(String(address), 90);
    const bytes = Uint8Array.from(bech32.fromWords(decoded.words));
    if (decoded.prefix !== CHAIN.addressPrefix || bytes.length !== 20) {
      throw new Error("wrong prefix or length");
    }
    return String(address);
  } catch {
    throw new Error("Recipient must be a valid 20-byte ipi address");
  }
}

export function addressFromEthPubkey(compressedPubkey) {
  const point = secp256k1.Point.fromBytes(compressedPubkey);
  const uncompressed = point.toBytes(false);
  const addressBytes = keccak_256(uncompressed.slice(1)).slice(-20);
  return bech32.encode(CHAIN.addressPrefix, bech32.toWords(addressBytes));
}

export function requireMatchingKeplrKey(key) {
  if (!key || !(key.pubKey instanceof Uint8Array) || key.pubKey.length !== 33) {
    throw new Error("Keplr did not return a compressed ethsecp256k1 public key");
  }
  validateAddress(key.bech32Address);
  if (addressFromEthPubkey(key.pubKey) !== key.bech32Address) {
    throw new Error("Keplr returned the wrong IPI address for its public key");
  }
  return key;
}

export function parseBaseAccount(payload) {
  let account = payload?.account;
  while (account && !("account_number" in account) && !("accountNumber" in account)) {
    account = account.base_account ?? account.baseAccount ?? account.base_vesting_account
      ?? account.baseVestingAccount;
  }
  const accountNumber = account?.account_number ?? account?.accountNumber;
  const sequence = account?.sequence;
  if (!/^\d+$/.test(String(accountNumber)) || !/^\d+$/.test(String(sequence))) {
    throw new Error("Malformed BaseAccount response");
  }
  return { accountNumber: BigInt(accountNumber), sequence: BigInt(sequence) };
}

export function receiptFromInclusion(inclusion, details) {
  const code = Number(inclusion?.code);
  const height = String(inclusion?.height ?? "");
  const txHash = String(inclusion?.txhash ?? inclusion?.txHash ?? "").toUpperCase();
  if (code !== 0 || !/^[0-9A-F]{64}$/.test(txHash) || !/^[1-9]\d*$/.test(height)) {
    throw new Error(`Transaction was not successful (code ${Number.isFinite(code) ? code : "unknown"})`);
  }
  return Object.freeze({
    source: "real-keplr",
    txHash,
    height,
    code,
    result: inclusion.raw_log || inclusion.rawLog || "success",
    ...details,
  });
}

export function isAcceptedReceipt(receipt) {
  return receipt?.source === "real-keplr"
    && receipt.code === 0
    && /^[0-9A-F]{64}$/.test(receipt.txHash)
    && /^[1-9]\d*$/.test(receipt.height);
}

export async function confirmAndRefresh({
  waitForInclusion,
  refreshBalance,
  txHash,
  details,
}) {
  const inclusion = await waitForInclusion(txHash);
  const receipt = receiptFromInclusion(inclusion, details);
  const balanceAfter = await refreshBalance();
  return { receipt: { ...receipt, balanceAfter }, balanceAfter };
}
